import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { MessageCircle, Star, Nfc, QrCode, ArrowDown } from "lucide-react";
import { LineReveal } from "./Reveal";
import { IMAGES, WHATSAPP_URL, scrollToSection } from "@/lib/site";

const Stars = () => (
  <span className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className="h-4 w-4 fill-[#FBBC04] text-[#FBBC04]" />
    ))}
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const img1x = useTransform(sx, [-0.5, 0.5], [-14, 14]);
  const img1y = useTransform(sy, [-0.5, 0.5], [-10, 10]);
  const img2x = useTransform(sx, [-0.5, 0.5], [18, -18]);
  const img2y = useTransform(sy, [-0.5, 0.5], [12, -12]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero-section"
      className="relative overflow-hidden pt-16"
    >
      <div className="pointer-events-none absolute inset-0 hero-grid" />
      <div className="pointer-events-none absolute -top-32 left-1/2 h-[480px] w-[720px] -translate-x-1/2 rounded-full bg-[#4285F4]/10 blur-3xl" />

      <div className="relative mx-auto grid max-w-6xl items-center gap-14 px-5 pb-20 pt-14 sm:pt-20 lg:grid-cols-[1.05fr_0.95fr] lg:pb-28">
        <div>
          <LineReveal delay={0.05}>
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#4285F4]">
              <Nfc className="h-3.5 w-3.5" /> NFC + QR Code · Zero app · Zero abbonamenti
            </span>
          </LineReveal>

          <h1 className="font-display mt-6 text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            <LineReveal delay={0.15}>Un solo tap.</LineReveal>
            <LineReveal delay={0.28}>
              <span>
                <span className="text-[#4285F4]">Cinque stelle</span> su
              </span>
            </LineReveal>
            <LineReveal delay={0.41}>Google, ogni giorno.</LineReveal>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.8 }}
            className="mt-6 max-w-lg text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            Le carte e gli espositori <strong className="text-slate-900">TapGoogle.com</strong> portano i tuoi clienti
            direttamente sulla pagina recensioni di Google: avvicinano lo smartphone o inquadrano il QR e in
            3 secondi lasciano 5 stelle. Più reputazione, più clienti nuovi, più clienti che tornano.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="group flex items-center gap-2.5 rounded-full bg-[#25D366] px-7 py-3.5 text-base font-semibold text-white shadow-xl shadow-green-500/30 transition-all duration-300 hover:scale-105 hover:bg-[#1EBE5D] hover:shadow-green-500/40"
            >
              <MessageCircle className="h-5 w-5 transition-transform duration-300 group-hover:-rotate-12" />
              Richiedi preventivo su WhatsApp
            </a>
            <button
              onClick={() => scrollToSection("#come-funziona")}
              data-testid="hero-how-cta"
              className="group flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3.5 text-base font-semibold text-slate-700 transition-colors duration-300 hover:border-[#4285F4] hover:text-[#4285F4]"
            >
              Scopri come funziona
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-500"
          >
            <span className="flex items-center gap-2"><Stars /> Pensato per il 5.0</span>
            <span className="flex items-center gap-2"><Nfc className="h-4 w-4 text-[#4285F4]" /> Chip NFC NTAG213</span>
            <span className="flex items-center gap-2"><QrCode className="h-4 w-4 text-[#4285F4]" /> QR di backup incluso</span>
          </motion.div>
        </div>

        <div className="relative mx-auto w-full max-w-md lg:max-w-none" data-testid="hero-visual">
          <div className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full spotlight" />
          <motion.div
            style={{ x: img1x, y: img1y }}
            initial={{ opacity: 0, y: 60, rotate: -8 }}
            animate={{ opacity: 1, y: 0, rotate: -4 }}
            transition={{ delay: 0.5, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 w-[72%] overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/15"
          >
            <img src={IMAGES.tapDemo} alt="Carta NFC TapGoogle.com: avvicina lo smartphone e lascia la recensione su Google" className="h-full w-full object-cover" />
          </motion.div>
          <motion.div
            style={{ x: img2x, y: img2y }}
            initial={{ opacity: 0, y: 60, rotate: 8 }}
            animate={{ opacity: 1, y: 0, rotate: 5 }}
            transition={{ delay: 0.7, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="absolute -bottom-10 right-0 z-20 w-[52%] overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/20"
          >
            <img src={IMAGES.cards} alt="Carta NFC Google fronte bianco e retro nero" className="h-full w-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.1, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="absolute -right-2 top-6 z-30 flex items-center gap-2 rounded-2xl border border-slate-100 bg-white px-4 py-2.5 shadow-xl shadow-slate-900/10"
          >
            <Stars />
            <span className="text-sm font-bold text-slate-900">+1 recensione</span>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.3, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="absolute -left-3 bottom-16 z-30 flex items-center gap-2 rounded-2xl border border-slate-100 bg-white px-4 py-2.5 shadow-xl shadow-slate-900/10"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-50">
              <Nfc className="h-4 w-4 text-[#4285F4]" />
            </span>
            <span className="text-sm font-bold text-slate-900">Tap · 3 secondi</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
